// Task 6: Admin-only "Audit Request — PSA status mapping" card, rendered on the dashboard's
// existing Settings page (already gated ADMIN_ONLY at the route level — see App.jsx — so no
// extra per-card RBAC check is needed here).
//
// Loads the live Autotask Projects `status` picklist (GET /psa-status-values, a proxy of
// Autotask-MCP's get_project_statuses) and the admin-configured Approved/Declined id sets
// (GET /psa-status-map), lets the admin pick which live statuses mean "approved" and which
// mean "declined" via two multi-selects, and saves via PUT (admin-key gated server-side —
// see routes.put_psa_status_map). A status id present in both lists is rejected CLIENT-SIDE
// here (the onChange handlers below refuse the change and show an inline error) as well as
// SERVER-SIDE (422) — defence in depth, not a substitute for either check.
//
// Styling: this card intentionally borrows the scoping/Audit-Request feature's own
// Claude visual language (`Card`/`Kicker`/`CardH2`/`Intro`/`Primary` from `../tender/ui`,
// `claudeDark` tokens, which follow the app's light/dark toggle) — it is
// Audit-Request domain content living on a shared admin page. antd's Select takes its
// styling from the root ConfigProvider in App.jsx (spec 2026-09-24 §7); the card wraps no
// theme of its own.
import { useEffect, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Select, Tag } from "antd";
import { Card, Kicker, CardH2, Intro, Primary, ErrorBanner } from "../tender/ui";
import { claudeDark } from "../../tokens";
import { scopingApi } from "../../scopingApi";

const FieldLabel = ({ children }) => (
  <div
    style={{
      display: "block",
      marginBottom: 6,
      color: claudeDark.textSecondary,
      font: `500 12px ${claudeDark.fontSans}`,
    }}
  >
    {children}
  </div>
);

const SuccessBanner = ({ children }) => (
  <div
    role="status"
    style={{
      background: claudeDark.status.completed.bg,
      border: `1px solid ${claudeDark.status.completed.border}`,
      color: claudeDark.status.completed.text,
      borderRadius: 10,
      padding: "12px 16px",
      marginBottom: 18,
      font: `500 13px ${claudeDark.fontSans}`,
    }}
  >
    {children}
  </div>
);

// Renders each selected multi-select value as a pill toned from claudeDark.status —
// completed (green) for the Approved select, failed (red) for the Declined select —
// the same tone convention the overview table and BriefPhase badges use for these
// exact two classifications.
function tonedTagRender(tone) {
  return ({ label, closable, onClose }) => (
    <Tag
      onMouseDown={(e) => e.preventDefault()}
      closable={closable}
      onClose={onClose}
      style={{
        background: tone.bg,
        color: tone.text,
        border: `1px solid ${tone.border}`,
        borderRadius: 999,
        marginInlineEnd: 4,
      }}
    >
      {label}
    </Tag>
  );
}

export default function PsaStatusMappingCard() {
  const [approved, setApproved] = useState([]);
  const [declined, setDeclined] = useState([]);
  const [overlapError, setOverlapError] = useState(null);
  const [saveResult, setSaveResult] = useState(null);

  const { data: values, isLoading: loadingValues } = useQuery({
    queryKey: ["scoping-psa-status-values"],
    queryFn: scopingApi.getPsaStatusValues,
  });
  const { data: mapping, isLoading: loadingMap } = useQuery({
    queryKey: ["scoping-psa-status-map"],
    queryFn: scopingApi.getPsaStatusMap,
  });

  // Seed local selection state from the stored mapping once it loads. Re-syncing whenever
  // `mapping` changes (not just on first mount) means a save-then-refetch also lands
  // cleanly, matching react-query's own cache-driven re-render.
  useEffect(() => {
    if (mapping) {
      setApproved(mapping.approved || []);
      setDeclined(mapping.declined || []);
    }
  }, [mapping]);

  const options = (values?.statuses || []).map((s) => ({ value: s.value, label: s.label }));
  const idToLabel = (id) => options.find((o) => o.value === id)?.label ?? id;

  const save = useMutation({
    mutationFn: () => scopingApi.savePsaStatusMap({ approved, declined }),
    onSuccess: () => setSaveResult({ type: "success", message: "PSA status mapping saved." }),
    onError: (err) =>
      setSaveResult({ type: "error", message: `Save failed: ${err.message}` }),
  });

  const handleApprovedChange = (next) => {
    const overlap = next.filter((v) => declined.includes(v));
    if (overlap.length > 0) {
      setOverlapError(
        `Already marked Declined — remove from Declined first: ${overlap.map(idToLabel).join(", ")}`,
      );
      return; // refuse the change; Approved stays as it was
    }
    setOverlapError(null);
    setSaveResult(null);
    setApproved(next);
  };

  const handleDeclinedChange = (next) => {
    const overlap = next.filter((v) => approved.includes(v));
    if (overlap.length > 0) {
      setOverlapError(
        `Already marked Approved — remove from Approved first: ${overlap.map(idToLabel).join(", ")}`,
      );
      return; // refuse the change; Declined stays as it was
    }
    setOverlapError(null);
    setSaveResult(null);
    setDeclined(next);
  };

  return (
    <>
      <Card>
        <Kicker>Audit Request</Kicker>
        <CardH2>PSA status mapping</CardH2>
        <Intro>
          Maps live Autotask project statuses to Approved / Declined for the Audit
          Request opportunity tracker. Any status not listed below reads as Pending.
        </Intro>

        {overlapError && <ErrorBanner role="alert">{overlapError}</ErrorBanner>}
        {saveResult?.type === "error" && (
          <ErrorBanner role="alert">{saveResult.message}</ErrorBanner>
        )}
        {saveResult?.type === "success" && <SuccessBanner>{saveResult.message}</SuccessBanner>}

        <div style={{ marginBottom: 16 }}>
          <FieldLabel>Approved</FieldLabel>
          <Select
            mode="multiple"
            style={{ width: "100%" }}
            options={options}
            value={approved}
            onChange={handleApprovedChange}
            loading={loadingValues}
            tagRender={tonedTagRender(claudeDark.status.completed)}
            placeholder="Autotask project statuses that mean Approved"
            aria-label="Approved statuses"
          />
        </div>

        <div style={{ marginBottom: 20 }}>
          <FieldLabel>Declined</FieldLabel>
          <Select
            mode="multiple"
            style={{ width: "100%" }}
            options={options}
            value={declined}
            onChange={handleDeclinedChange}
            loading={loadingValues}
            tagRender={tonedTagRender(claudeDark.status.failed)}
            placeholder="Autotask project statuses that mean Declined"
            aria-label="Declined statuses"
          />
        </div>

        <Primary
          onClick={() => save.mutate()}
          loading={save.isPending}
          disabled={loadingMap || loadingValues}
        >
          Save mapping
        </Primary>
      </Card>
    </>
  );
}
